class StickyAddToCart extends HTMLElement {
  constructor() {
    super();
    this.buttonObserver = null;
    this.footerObserver = null;
    this.previewBarObserver = null;
    this.signUpObserver = null;
    this.buttonHidden = false;
    this.footerVisible = false;
    this.initialised = false;
    this.handleVariantUpdate = this.handleVariantUpdate.bind(this);
    this.handleNotifyClick = this.handleNotifyClick.bind(this);
  }

  connectedCallback() {
    // Move the element to <body> so the fixed bar escapes any section stacking
    // context.
    //
    // GUARDED, and the guard is load-bearing: appendChild MOVES the element,
    // which fires disconnectedCallback and then connectedCallback again.
    // Unguarded this re-entered forever and threw "Maximum call stack size
    // exceeded" on every product page, while also leaking an
    // IntersectionObserver pair per pass (only the last pair was ever stored,
    // so disconnectedCallback could not clean the rest up).
    //
    // The move re-enters this callback, and that second pass — where the
    // element is already a child of <body> — is the one that does the setup.
    if (this.parentElement !== document.body) {
      document.body.appendChild(this);
      return;
    }

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => this.init(), { once: true });
    } else {
      this.init();
    }

    // Keep the sticky button's availability in sync with the selected variant.
    document.addEventListener("variant:updated", this.handleVariantUpdate);
  }

  init() {
    if (this.initialised) return;

    const originalButton = document.querySelector('.pdp-atc button[type="submit"]');

    // The footer sections are not wrapped in a single element. Shopify renders
    // a section GROUP by putting a `shopify-section-group-<name>` CLASS on each
    // member section, so there is no `#shopify-section-group-footer` to find —
    // the previous selector always returned null and the bar therefore never
    // hid over the footer. Observe the first section of the group instead.
    const footer = document.querySelector(".shopify-section-group-footer-group");

    if (!originalButton) {
      return;
    }

    this.initialised = true;

    this.watchSignUp();

    // Watch original ATC button
    this.buttonObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          this.buttonHidden = !entry.isIntersecting;
          this.updateVisibility();
        });
      },
      {
        root: null,
        rootMargin: "0px",
        threshold: 0,
      },
    );
    this.buttonObserver.observe(originalButton);

    // Watch footer — hide sticky bar when footer is visible
    if (footer) {
      this.footerObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.footerVisible = entry.isIntersecting;
            this.updateVisibility();
          });
        },
        {
          root: null,
          rootMargin: "0px",
          threshold: 0,
        },
      );
      this.footerObserver.observe(footer);
    }

    // On a theme preview link Shopify pins its own preview bar to the bottom
    // of the viewport, on top of this one, so a reviewer never sees the sticky
    // button on load. The bar can arrive after DOMContentLoaded, so look again
    // once the page has loaded. Shoppers on the live store never get it.
    if (!this.watchPreviewBar()) {
      window.addEventListener("load", () => this.watchPreviewBar(), { once: true });
    }
  }

  // Sit directly above Shopify's preview bar for as long as it takes up
  // space, following it when the reviewer hides or shows it.
  watchPreviewBar() {
    const previewBar = document.getElementById("PBarNextFrameWrapper");
    if (!previewBar || this.previewBarObserver || typeof ResizeObserver === "undefined") {
      return Boolean(this.previewBarObserver);
    }

    this.previewBarObserver = new ResizeObserver(() => {
      const height = Math.round(previewBar.getBoundingClientRect().height);
      this.style.bottom = height > 0 ? `${height}px` : "";
    });
    this.previewBarObserver.observe(previewBar);
    return true;
  }

  // The "Notify me when it's back" block shows only while the hat or the chosen
  // size is sold out. While it shows, the bar offers "Notify me" in place of a
  // disabled SOLD OUT button. No sign-up block on the page: nothing changes.
  watchSignUp() {
    const signUp = document.getElementById("back-in-stock");
    const notify = this.querySelector("[data-notify-button]");
    if (!signUp || !notify) return;

    const sync = () => this.toggleAttribute("data-notify", !signUp.hidden);
    sync();

    this.signUpObserver = new MutationObserver(sync);
    this.signUpObserver.observe(signUp, { attributes: true, attributeFilter: ["hidden"] });
    notify.addEventListener("click", this.handleNotifyClick);
  }

  // Take the shopper to the email field, below the sticky header. On a hat
  // that is sold out in every size and has no size chosen yet, the sign-up
  // only asks for a size, so take them to the size picker instead.
  handleNotifyClick() {
    const signUp = document.getElementById("back-in-stock");
    if (!signUp) return;

    const picker = document.querySelector("variant-selector");
    if (signUp.classList.contains("is-choosing") && typeof picker?.promptForSelection === "function") {
      picker.promptForSelection();
      return;
    }

    const header =
      parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--header-height")) || 0;
    const top = signUp.getBoundingClientRect().top + window.scrollY - header - 16;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    window.scrollTo({ top: Math.max(top, 0), behavior: reduced ? "auto" : "smooth" });
    signUp.querySelector('input[type="email"]')?.focus({ preventScroll: true });
  }

  updateVisibility() {
    this.classList.toggle("visible", this.buttonHidden && !this.footerVisible);
  }

  // Sync the sticky Add-to-Cart button's disabled state + label with the newly
  // selected variant. Driven by the `variant:updated` event that
  // variant-selector.js dispatches, mirroring how the main button updates.
  handleVariantUpdate(event) {
    const variant = event.detail?.variant;
    const button = this.querySelector('button[type="submit"]');
    if (!variant || !button) return;

    button.disabled = !variant.available;

    const textEl = button.querySelector(".atc-button__text");
    if (textEl) {
      textEl.textContent = variant.available
        ? button.dataset.addLabel || textEl.textContent
        : button.dataset.soldOutLabel || textEl.textContent;
      // Let the accessible name follow the visible label, not the first state.
      button.removeAttribute("aria-label");
    }
  }

  disconnectedCallback() {
    if (this.buttonObserver) {
      this.buttonObserver.disconnect();
      this.buttonObserver = null;
    }
    if (this.footerObserver) {
      this.footerObserver.disconnect();
      this.footerObserver = null;
    }
    if (this.previewBarObserver) {
      this.previewBarObserver.disconnect();
      this.previewBarObserver = null;
    }
    if (this.signUpObserver) {
      this.signUpObserver.disconnect();
      this.signUpObserver = null;
    }
    this.initialised = false;
    document.removeEventListener("variant:updated", this.handleVariantUpdate);
  }
}

customElements.define("sticky-add-to-cart", StickyAddToCart);
